import 'reflect-metadata'
import { type ServiceIdentifier, injectable } from 'inversify'

import { AltContainer } from './DI'
import type { DiConfig } from './nuxt/defineDiConfig'

@injectable()
export class AltApplication {
  readonly providers: Set<DiConfig> = new Set()
  readonly providersResolveHooks: (() => unknown)[] = []
  readonly container: AltContainer = new AltContainer()

  registerDiConfigs(...configs: DiConfig[]): this {
    configs.forEach((config: DiConfig) => {
      this.providers.add(config)
    })
    return this
  }

  async resolveDiConfigs(): Promise<Array<DiConfig>> {
    const configs = Array.from(this.providers)

    const results = await Promise.allSettled(
      configs.map(config => {
        try {
          return config.setup(this.container, this)
        } catch (e) {
          // если setup бросает синхронно
          return Promise.reject(e)
        }
      }),
    )

    results.forEach((result, index) => {
      const config = configs[index]
      if (result.status === 'rejected') {
        try {
          console.warn('DiConfig setup failed:', config?.name ?? config, result.reason)
        } catch (err) {
          console.warn('DiConfig setup failed (unable to stringify config)', result.reason)
        }
      }
      // Удаляем конфиг независимо от результата
      this.providers.delete(config)
    })

    await Promise.allSettled(this.providersResolveHooks.map(hook => hook()))

    return configs
  }

  onProvidersResolve(hook: () => void | Promise<void>) {
    this.providersResolveHooks.push(hook)
  }

  boot(): Promise<Array<DiConfig>> {
    this.container.bind<AltApplication>(AltApplication).toConstantValue(this)
    return this.resolveDiConfigs()
  }

  get<T>(identifier: ServiceIdentifier<T>): T {
    return this.container.get<T>(identifier)
  }

  getAll<T>(identifier: ServiceIdentifier<T>): T[] {
    return this.container.getAll<T>(identifier)
  }

  destroy() {
    this.providers.clear()
    this.container.unbindAll()
  }

  constructor() {
    this.container.bind(AltContainer).toConstantValue(this.container)

    return new Proxy<this>(this, {
      get(
        target: AltApplication,
        prop: string | symbol,
        receiver: AltApplication,
      ) {
        if (target.container.isBound(prop)) {
          return target.get(prop)
        }
        return Reflect.get(target, prop, receiver)
      },
      ownKeys(target: AltApplication): ArrayLike<string | symbol> {
        return Reflect.ownKeys(target)
      },
      has(target: AltApplication, p: string | symbol): boolean {
        return (
          new Set(Reflect.ownKeys(target)).has(p) || Reflect.has(target, p)
        )
      },
    })
  }
}
